import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function ReverbLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/reverb";
  const pageTitle = "Reverb in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what reverb is, how reverberation works, the main types of reverb, common controls, and how reverb is used in music production, film, and sound design.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "reverb",
          "audio reverb",
          "reverberation",
          "reverb plugin",
          "room reverb",
          "plate reverb",
          "spring reverb",
          "convolution reverb",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Reverb" },
          { "@type": "Thing", name: "Reverberation" },
          { "@type": "Thing", name: "Room acoustics" },
          { "@type": "Thing", name: "Plate reverb" },
          { "@type": "Thing", name: "Convolution reverb" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Reverb",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Reverb recreates the sound of reflections in a space, adding depth, size, and ambience to audio.",
      icon: <WavesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It helps listeners perceive distance, dimension, atmosphere, and emotional space in a mix.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, drums, guitars, synths, film dialogue, sound design, orchestral music, and ambient production.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const reverbTypes = [
    {
      name: "Room Reverb",
      description:
        "Simulates a small or medium room. Useful for intimacy, realism, and giving sounds a natural sense of place.",
    },
    {
      name: "Hall Reverb",
      description:
        "Models a larger performance space with a longer, lush decay. Common in orchestral, cinematic, and expansive mixes.",
    },
    {
      name: "Chamber Reverb",
      description:
        "Inspired by real echo chambers, often dense and rich with more clarity than a huge hall sound.",
    },
    {
      name: "Plate Reverb",
      description:
        "Originally created with vibrating metal plates. Known for a smooth, dense decay and widely used on vocals and snares.",
    },
    {
      name: "Spring Reverb",
      description:
        "Uses springs instead of a plate or room model. It has a recognizable boingy, metallic character, especially in guitar amps.",
    },
    {
      name: "Digital Reverb",
      description:
        "Algorithmic reverb created in hardware or software. Extremely flexible and capable of a huge range of spaces and textures.",
    },
    {
      name: "Convolution Reverb",
      description:
        "Uses impulse responses captured from real spaces or devices to recreate realistic acoustic environments.",
    },
    {
      name: "Shimmer Reverb",
      description:
        "A modern atmospheric style that combines reverb with pitch shifting, often producing huge, floating textures.",
    },
    {
      name: "Gated Reverb",
      description:
        "Cuts the reverb tail off abruptly, creating a dramatic and punchy sound that became famous on 1980s drums.",
    },
  ];

  const parameters = [
    {
      name: "Pre-Delay",
      description:
        "The gap between the dry sound and the start of the reverb. Helps preserve clarity and separation.",
    },
    {
      name: "Decay / RT60",
      description:
        "How long the reverb takes to fade away. Longer settings feel larger and more dramatic.",
    },
    {
      name: "Room Size",
      description:
        "Changes the sense of scale and the density of reflections.",
    },
    {
      name: "Damping",
      description:
        "Controls how quickly highs or lows die away in the tail, shaping brightness and realism.",
    },
    {
      name: "Dry / Wet Mix",
      description:
        "Balances the original signal with the reverberated signal.",
    },
    {
      name: "EQ / Filtering",
      description:
        "Used to keep reverb from becoming muddy or harsh by removing excess lows or highs.",
    },
  ];

  const beginnerTips = [
    "Put an EQ after reverb to tame mud, harshness, or excessive brightness.",
    "High-pass the reverb return so low frequencies do not build up unnecessarily.",
    "Use pre-delay to keep vocals and lead sounds clear while still sounding spacious.",
    "Use less reverb than you think you need. Subtle settings often sound more polished.",
    "Different sources may need different reverb types. A snare, vocal, and synth pad rarely want exactly the same space.",
  ];

  const applications = [
    "Placing vocals into a believable or stylized space",
    "Adding realism to sampled or virtual instruments",
    "Creating depth and front-to-back layering in a mix",
    "Enhancing transitions, swells, and special effects",
    "Matching dialogue and sound effects to on-screen environments in film and games",
  ];

  const relatedLinks = [
    {
      title: "Delay",
      to: "/learning/delay",
      description:
        "Explore how delay creates distinct repeats over time, while reverb creates dense reflections and ambience.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "See how chorus adds width and shimmer in a different way than spatial ambience from reverb.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how EQ helps shape reverb returns so they stay clear instead of muddy or harsh.",
    },
    {
      title: "Stereo Imaging",
      to: "/learning/stereo-imaging",
      description:
        "Understand how reverb placement and width can influence depth and spaciousness in a mix.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Reverb | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Reverb
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Reverb, short for reverberation, is one of the most important
                      effects in music, acoustics, and sound design. It shapes how we
                      perceive space, size, distance, and atmosphere. Whether you are
                      hearing a choir in a cathedral or a close vocal in a modern pop
                      mix, reverb plays a huge role in how that sound feels.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Reverb can make a sound feel intimate, massive, natural, dreamy,
                      or surreal. Used well, it adds depth and emotion. Used carelessly,
                      it can blur detail and make a mix feel distant or muddy.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WavesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Is Reverb?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Reverb is the persistence of sound after the source stops. It
                    happens because sound waves bounce off surfaces such as walls,
                    ceilings, floors, and objects, creating a dense series of
                    reflections that gradually decay over time.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Unlike a clear echo, which is heard as a distinct repeat,
                    reverb is made of many overlapping reflections arriving very
                    quickly. Those reflections blend into a smooth tail or wash
                    that tells our ears about the environment around the sound.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Reverb is part of how humans understand space. It helps us
                    sense whether a sound is close or far away, in a tiny room or
                    a huge hall, dry and intimate or distant and atmospheric.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Why Reverb Matters
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Reverb adds realism, depth, and emotion. It can make a
                      sound feel larger than life, or it can simply place that
                      sound in a believable environment.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Producers and mixers use reverb to make vocals feel close
                      or distant, to glue sounds together, to add atmosphere to
                      instruments, and to create transitions and cinematic
                      textures. At the same time, too much reverb can make a mix
                      cloudy and reduce intelligibility, so balance matters.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        RT60 and Room Acoustics
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      RT60 is the time it takes for the reverb level to decay by
                      60 dB. It is one of the most important measurements in room
                      acoustics because it gives a sense of how live or dry a
                      space is.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Small treated rooms may have very short reverb times, while
                      cathedrals and large halls may ring for several seconds.
                      Shorter times often improve speech clarity, while longer
                      times can sound beautiful for orchestral or ambient music.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Classical acoustics formulas such as Sabine’s equation and
                      Eyring’s equation are still used to estimate how a room will
                      behave.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Early Reflections and Reverb Tail
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The first reflections that arrive shortly after the direct
                    sound are called early reflections. These help the ear judge
                    the size and shape of a space. After that comes the denser,
                    more continuous reverb tail, which creates the lingering sense
                    of ambience.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    This distinction matters in production because changing the
                    early reflections, pre-delay, and decay shape can dramatically
                    change how near or far a sound feels in the mix.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1.15fr 0.85fr", md: "1.15fr 0.85fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WavesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                      >
                        Types of Reverb
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {reverbTypes.map((type) => (
                        <Paper
                          key={type.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {type.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {type.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                      >
                        Reverb Parameters
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {parameters.map((param) => (
                        <Paper
                          key={param.name}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {param.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {param.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Reverb in Music and Sound Design
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {applications.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      In film and games, reverb is often used to match dialogue
                      and effects to a visual environment. In music, it is often
                      used to shape emotional distance and depth.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Tips for Beginners
                      </Typography>
                    </Stack>

                    <Stack spacing={1.15}>
                      {beginnerTips.map((tip, index) => (
                        <Stack key={index} direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {tip}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final Thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Reverb is much more than a simple effect. It is part of how we
                    understand the world through sound. In production, it can make
                    something feel intimate, massive, natural, dreamy, or surreal.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The most effective reverb choices usually come from listening
                    carefully to what the sound needs. Sometimes that means a huge
                    cinematic tail. Sometimes it means a tiny amount of room
                    ambience that you barely notice until it is gone.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}
// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

// export default function ReverbLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <>
//         <div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//           <h1 className='h1-A' ref={topContainer}>Reverb</h1>
//           <Divider className="mobile-card-divider" />
//             <p className='p-A'>Reverb — short for reverberation — is one of the most fundamental and widely used effects in music, sound design, and acoustics. From cathedral choirs to guitar solos drenched in atmosphere, reverb has shaped the way we experience sound both naturally and artificially.</p>
//           <h2 className='h2-A'>What Is Reverb?</h2>
//             <p className='p-A'>Reverb is the persistence of sound after the original source has stopped. It occurs when sound waves reflect off surfaces like walls, ceilings, floors, and objects in a space, creating a dense, decaying tail of reflections that our ears perceive as ambiance.</p>
//             <p className='p-A'>Unlike an echo — which you can hear as a distinct repetition of a sound — reverberation consists of many overlapping reflections that arrive within milliseconds of each other. The result is a smooth, continuous wash of sound that helps us determine the size, shape, and material of the environment we’re in.</p>
//             <p className='p-A'>Reverb is everywhere: in nature (caves, forests), in architecture (cathedrals, theaters), and in recordings. It's a crucial part of how we interpret spatial information through hearing.</p>
//           <h2 className='h2-A'>Why Reverb Matters</h2>
//           <p className='p-A'>Reverb adds realism, dimension, and depth to sound. It gives recordings a sense of place. For music producers and mix engineers, reverb is a powerful creative tool. It can make a vocal feel intimate or distant, give drums punch or atmosphere, or make a guitar solo soar.</p>
//           <p className='p-A'>But reverb is a double-edged sword. While it can enrich a track, too much reverb can make a mix sound muddy or obscure speech intelligibility — especially in dialogue-heavy content like podcasts or film. In audio engineering, there's a delicate balance between "wet" (reverberated) and "dry" (unaffected) sound.</p>
//           <h2 className='h2-A'>Reverb Time (RT60) and Acoustics</h2>
//           <p className='p-A'>The reverberation time, often abbreviated RT60, refers to the time it takes for reverb to decay by 60 decibels. It's one of the most important metrics in room acoustics.</p>
//           <p className='p-A'>A cathedral might have an RT60 of 6–8 seconds, while a small recording booth might have an RT60 of less than 0.3 seconds. For speech clarity, rooms are often designed with shorter reverb times (0.5 to 1.0 seconds), while concert halls for orchestral music benefit from longer times (1.8 to 2.2 seconds).</p>
//           <h3 className='h3-A'>Sabine’s Equation</h3>
//           <p className='p-A'>The study of reverb began in the late 19th century with Wallace Clement Sabine, who developed the famous Sabine equation:</p>
//           <p className='p-A'><strong>RT60 ≈ 0.161 × V / A</strong></p>
//           <p className='p-A'>Where:</p>
//           <ul className="a">
//             <li>V = volume of the room (in m³)</li>
//             <li>A = total absorption (in sabins)</li>
//           </ul>
//           <h3 className='h3-A'>Eyring’s Equation</h3>
//           <p className='p-A'>For rooms with high absorption, Eyring's formula provides a more accurate model:</p>
//           <p className='p-A'>RT60 ≈ -0.161 × V / (S × ln(1 - a))</p>
//           <p className='p-A'>Where:</p>
//           <ul className="a">
//             <li>S = surface area of the room</li>
//             <li>a = average absorption coefficient</li>
//           </ul>
//           <p className='p-A'>These equations are still widely used in architectural acoustics today.</p>
//           <h2 className='h2-A'>Early Reflections and Perception</h2>
//           <p>When a sound is produced in a space, the first reflections (arriving within ~50 milliseconds) are known as early reflections. They give us cues about the size and shape of the space. The later, denser cluster of reflections is the reverb tail.</p>         
//           <p className='p-A'>Our brains are highly sensitive to this information. Even subtle changes in reverb can influence how we perceive the position, size, and importance of a sound source.</p>
//           <h2 className='h2-A'>Types of Reverb</h2>
//           <p className='p-A'>Reverb can be natural or artificially generated. Here are the main types used in audio production:</p>
//           <ul className="a">
//             <li className="li-A"><strong>Room Reverb - </strong>Simulates a small- to medium-sized space. Adds intimacy and realism. Great for pop, rock, and dialogue.</li>
//             <li className="li-A"><strong>Hall Reverb - </strong>Mimics the acoustics of a large concert hall. Long decay and lush reflections. Ideal for orchestral music, pads, and cinematic scoring.</li>
//             <li className="li-A"><strong>Chamber Reverb - </strong>Historically created by placing a speaker and mic in a reverberant room. Chamber reverb sounds thick and rich, but with more clarity than hall reverb.<br />Famous examples: Abbey Road Studios, Capitol Studios</li>
//             <li className="li-A"><strong>Plate Reverb - </strong>
            
//               Invented in the 1950s (e.g., EMT 140), plate reverb creates ambiance using vibrations in a sheet of metal. Known for its dense, smooth decay. Often used on vocals, snares, and guitars.<br /> A compact mechanical design using springs instead of metal plates. Common in guitar amps and Hammond organs. Produces a metallic, “boingy” character.
//               <br />
//               </li>
              
//             <li className="li-A"><strong>Spring Reverb - </strong>
//             A compact mechanical design using springs instead of metal plates. Common in guitar amps and Hammond organs. Produces a metallic, “boingy” character.
//               <br />
//               <br />

//               - Famous in surf rock and dub reggae.
//               <br />
//               <br />

//               - Units: Fender Twin Reverb, Demeter RV-1, RE-201 Space Echo

//               <br/>
//               <br/>
//             <li className="li-A"><strong>Digital Reverb - </strong>Introduced in the 1970s with the EMT 250, digital reverb simulates acoustic environments using algorithms and delay lines. Extremely versatile.</li>
//           </li>
//           <li className="li-A"><strong>Convolution Reverb - </strong>Uses real-world impulse responses (IRs) to model the exact reverberation of a physical space. Often used in film and post-production for realism.</li>
//           <li className="li-A"><strong>Shimmer Reverb - </strong>A modern, atmospheric type that adds pitch-shifting (usually an octave up or down) to the reverb tail. Perfect for ambient music and synth pads.</li>
//           <li className="li-A"><strong>Gated Reverb - </strong>Made famous in the 1980s (Phil Collins' “In the Air Tonight”), gated reverb abruptly cuts off the reverb tail using a noise gate — creating a powerful, punchy effect on drums.</li>

//           </ul>
//           <h2 className='h2-A'>Reverb Parameters Explained</h2>
//           <p className='p-A'>When using reverb, understanding key parameters is crucial:</p>
//           <ul className="a">
//           <li className="li-A"><strong>Pre-delay: </strong>Time between dry signal and onset of reverb. Adds separation.</li>
//           <li className="li-A"><strong>Decay (RT60): </strong>Duration of the reverb tail.</li>
//           <li className="li-A"><strong>Room Size: </strong>Affects the density and length of reflections.</li>
//           <li className="li-A"><strong>Damping: </strong>Controls how high and low frequencies decay.</li>
//           <li className="li-A"><strong>Dry/Wet Mix: </strong>Blend between unprocessed (dry) and reverberated (wet) signal.</li>
//           <li className="li-A"><strong>EQ: </strong>Tailor reverb response using high-pass or low-pass filters to avoid muddiness.</li>


// </ul>
// <h2 className='h2-A'>Reverb in Music Production</h2>       
//           <p className='p-A'>Most modern music is recorded “dry” in acoustically treated rooms. Reverb is then added during the mixing process using plugins or outboard gear.</p>
//           <p className='p-A'>Producers use reverb to:</p>

//           <ul className="a">
//           <li className="li-A">Place vocals in a virtual space</li>
//           <li className="li-A">Add realism to virtual instruments</li>
//           <li className="li-A">Create transitions and effects</li>
//           <li className="li-A">Make mixes sound cohesive</li>

// </ul>
//          <h2 className='h2-A'>Reverb in Film and Sound Design</h2>   
//          <p className='p-A'>In film and game audio, reverb recreates physical spaces or enhances emotional cues. A gunshot in a parking garage has different reflections than one in a forest. Sound designers often use convolution reverb to match dialogue to on-screen environments.</p>
//          <p className='p-A'>Foley artists also use reverb to add depth to footsteps, door creaks, and environmental sounds.</p>
//          <p className='p-A'>Example: Hans Zimmer’s score for Interstellar uses long, celestial reverbs to reflect the vastness of space.</p>
         
         
//          <h2 className='h2-A'>Notable Hardware</h2>         
          
//           <p className='p-A'>Here are some legendary and modern tools to explore:</p>
//           <h3 className='h3-A'>Classic Hardware Units:</h3>
//           <ul className="a">
//             <li className="li-A"><strong>EMT 140 - </strong>Plate reverb standard</li>
//             <li className="li-A"><strong>Lexicon 224 - </strong>Digital hall reverb, heard on countless records</li>
//             <li className="li-A"><strong>AMS RMX16 - </strong>80s digital staple (gated reverb)</li>
//             <li className="li-A"><strong><a href='https://www.roland.com/global/products/rc_re-201_space_echo/' target="_blank" rel="noopener noreferrer">RE-201 Space Echo</a> - </strong>Tape echo with spring reverb</li>
//             <li className="li-A"><strong><a href='https://www.fender.com/products/65-twin-reverb' target="_blank" rel="noopener noreferrer">Fender Twin Reverb</a> - </strong>Spring tank in an amp</li>

// </ul>
// <div style={{ width:'95%', maxWidth:'400px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="1965 Fender Twin Reverb Blonde" bottomTextLinkUrl="https://www.fender.com/products/65-twin-reverb" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Fender_Twin_Reverb_Blonde.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/d/d5/Fender_Twin_Reverb_Blonde.jpg" alt = "1965 Fender Twin Reverb Blonde"  title = "Paulus 2, CC BY-SA 4.0 <https://creativecommons.org/licenses/by-sa/4.0>, via Wikimedia Commons" />
//   </div>

// <h2 className='h2-A'>Reverb Tips for Beginners</h2>
// <br />
// <ul className="a">
//             <li className="li-A"><strong>Add an EQ after reverb </strong>to tame harshness or muddiness.</li>
//             <li className="li-A"><strong>High-pass your reverb </strong>to prevent low-end rumble.</li>
//             <li className="li-A"><strong>Fine-tune wet/dry levels  </strong>to highlight certain words or instruments.</li>
//             <li className="li-A"><strong>Less is more:  </strong>subtle reverb often sounds more natural.</li>

// </ul>
// <h2 className='h2-A'>Final Thoughts</h2> 
//           <p className='p-A'>Reverb is more than just an effect — it’s an essential ingredient in how we experience sound. Whether you're crafting a lo-fi bedroom track, designing a soundscape for film, or mixing a live concert, knowing how to wield reverb will elevate your work to a professional level.</p>
//           <p className='p-A'>From natural acoustics to plate reverbs and modern shimmer tails, the creative possibilities are endless. Explore, experiment, and let your ears guide you.</p>
//           <br />
//          <br />
//          <br />
//         <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
//       </div>


        
//         </>

//     )
// }